'use client'

import * as React from 'react'
import {
  addDays,
  differenceInCalendarDays,
  format,
  isAfter,
  isSameDay,
  startOfDay,
  subDays,
} from 'date-fns'
import type { DateRange as DayPickerRange } from 'react-day-picker'
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import { Button } from '../../components/button'
import { Calendar } from '../../components/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../components/popover'
import { useIsMobile } from '../../hooks/use-mobile'
import { cn } from '../../lib/utils'

export interface DateRangeValue {
  from: Date
  to: Date
}

export interface DateRangePreset {
  label: string
  days: number
  /**
   * Which way the window runs from today. `back` (the default) is "the last
   * N days", which is what a report asks. `forward` is "the next N days",
   * which is what a book asks: an orders list is mostly things that have not
   * happened yet, and a preset that can only look backwards is no use on it.
   */
  direction?: 'back' | 'forward'
}

const DEFAULT_PRESETS: DateRangePreset[] = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 14 days', days: 14 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
]

/**
 * WHAT A CLICK ON THE CALENDAR MEANS, given what is already drafted.
 *
 * Pure and exported, because this is the whole of the interaction and it was
 * wrong in a way nobody could see from the markup: left to itself,
 * react-day-picker treats a click on a FINISHED range as an edit, so
 * clicking a day before the current start kept the old end and handed back a
 * complete range. The caller applied it and shut the popover, having picked
 * an end date nobody chose.
 *
 *   A finished range -> start over, this click is the new `from`.
 *   A half-made one  -> take what day-picker says.
 *
 * `apply` is true only for a range spanning more than one day. A single day
 * is ambiguous at the moment it is clicked (it could be the start of a
 * longer one), so it waits for Apply.
 */
export function draftAfterClick(
  draft: DayPickerRange | undefined,
  next: DayPickerRange | undefined,
  clicked: Date
): { draft: DayPickerRange | undefined; apply: boolean } {
  if (draft?.from && draft.to) {
    return { draft: { from: clicked, to: undefined }, apply: false }
  }
  const complete = Boolean(
    next?.from && next.to && !isSameDay(next.from, next.to)
  )
  return { draft: next, apply: complete }
}

/**
 * THE RANGE A PRESET MEANS, which is the bug this is extracted for.
 *
 * A backward preset used to run back from `limit`. That is right only while
 * the limit IS today, which is its default - and catastrophic otherwise: a
 * caller passing `maxDate` two years ahead (a book runs to next Christmas)
 * turned "Last 7 days" into the last week of 2028 and showed an empty page.
 *
 * The limit says how far the calendar lets you PICK. It has never said what
 * "last" means. Backward presets anchor on today, clamped to the limit so a
 * surface whose limit is in the past is not offered days beyond it.
 */
export function presetRange(
  preset: DateRangePreset,
  today: Date,
  limit: Date
): DateRangeValue {
  const from = startOfDay(today)
  if (preset.direction === 'forward') {
    return { from, to: addDays(from, preset.days - 1) }
  }
  const end = isAfter(from, limit) ? startOfDay(limit) : from
  return { from: subDays(end, preset.days - 1), to: end }
}

function rangeLabel(value: DateRangeValue): string {
  const sameYear = value.from.getFullYear() === value.to.getFullYear()
  const fromLabel = format(value.from, sameYear ? 'd MMM' : 'd MMM yyyy')
  return `${fromLabel} – ${format(value.to, 'd MMM yyyy')}`
}

/**
 * A scrubbable date-range control for report surfaces: ◀ / ▶ slide the whole
 * window backward/forward by its own length (clamped to `maxDate`), and the
 * centre trigger opens preset shortcuts plus a two-month range calendar.
 */
export function DateRangeControl({
  value,
  onChange,
  presets = DEFAULT_PRESETS,
  maxDate,
  placeholder = 'Any dates',
  onClear,
  className,
}: {
  /**
   * NULL IS "NO RANGE AT ALL", not a range of nothing.
   *
   * A report always has a window: it is a report OF something, over a period.
   * A list does not: "every order" is the honest default, and forcing a
   * window on it would quietly hide rows while looking like it had not.
   *
   * With null the trigger reads `placeholder` and the scrub arrows are dead,
   * because there is no length to slide.
   */
  value: DateRangeValue | null
  onChange: (range: DateRangeValue) => void
  presets?: DateRangePreset[]
  /**
   * Latest selectable day, and where forward scrubbing clamps.
   *
   * Defaults to TODAY, which is right for a report and wrong for a book: a
   * pre-order list runs to next Christmas, so a caller showing one passes a
   * date well ahead.
   */
  maxDate?: Date
  /** What the trigger reads when there is no range. */
  placeholder?: string
  /**
   * Hand back "no range at all", which `onChange` cannot express: it is typed
   * to a complete range, so once a caller has one there is no way out of it.
   *
   * Supply it and the preset list gains an entry reading `placeholder`, which
   * is the honest place for it - "any dates" is the widest window, not a
   * separate kind of control. Leave it off and no such entry renders, so a
   * surface that genuinely requires a window (a report) cannot have it
   * cleared out from under it.
   */
  onClear?: () => void
  className?: string
}) {
  const isMobile = useIsMobile()
  const [open, setOpen] = React.useState(false)
  const [draft, setDraft] = React.useState<DayPickerRange | undefined>(
    value ?? undefined
  )

  React.useEffect(() => {
    setDraft(value ?? undefined)
  }, [value])

  const limit = startOfDay(maxDate ?? new Date())
  const lengthDays =
    value === null ? 0 : differenceInCalendarDays(value.to, value.from) + 1
  const atLimit =
    value === null || isSameDay(value.to, limit) || isAfter(value.to, limit)

  const scrub = (direction: -1 | 1) => {
    if (value === null) return
    const shift = lengthDays * direction
    let nextFrom = addDays(value.from, shift)
    let nextTo = addDays(value.to, shift)
    if (isAfter(nextTo, limit)) {
      nextTo = limit
      nextFrom = subDays(limit, lengthDays - 1)
    }
    onChange({ from: startOfDay(nextFrom), to: startOfDay(nextTo) })
  }

  /**
   * WHERE "LAST N DAYS" ENDS, which is today and not the limit.
   *
   * A backward preset used to run back from `limit`. That is right only while
   * the limit IS today, which is its default - and catastrophic otherwise: a
   * list passing `maxDate` two years ahead (a book runs to next Christmas)
   * turned "Last 7 days" into the last week of 2028 and showed an empty page.
   *
   * The limit says how far the calendar lets you PICK. It has never said what
   * "last" means. Still clamped, so a surface whose limit is in the past does
   * not offer days beyond it.
   */
  const today = startOfDay(new Date())
  const backAnchor = isAfter(today, limit) ? limit : today

  const applyPreset = (preset: DateRangePreset) => {
    // A forward preset runs from TODAY, not from the limit: the limit on a
    // forward-looking list is "as far ahead as anyone can book", which is not
    // a day anybody means by "the next 7 days".
    onChange(presetRange(preset, today, limit))
    setOpen(false)
  }

  /**
   * Which preset the current range IS, by length AND direction.
   *
   * Matching on length alone lit up both "Next 7 days" and "Last 7 days" at
   * once for a caller that offers both, because they are the same number of
   * days pointing opposite ways.
   */
  const activePreset =
    value === null
      ? null
      : (presets.find(
          (preset) =>
            preset.days === lengthDays &&
            (preset.direction === 'forward'
              ? isSameDay(value.from, today)
              : isSameDay(value.to, backAnchor))
        ) ?? null)

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <Button
        variant="outline"
        shape="square"
        size="icon-lg"
        className="size-11 sm:size-10"
        aria-label={`Previous ${String(lengthDays)} days`}
        disabled={value === null}
        onClick={() => {
          scrub(-1)
        }}
      >
        <ChevronLeftIcon className="size-4" />
      </Button>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          render={
            <Button
              variant="outline"
              shape="square"
              size="lg"
              // Fills the row on phones (thumb-sized, arrows at the edges);
              // fixed comfortable width on larger screens.
              className="h-11 min-w-0 flex-1 justify-start gap-2 font-normal tabular-nums sm:h-10 sm:min-w-[200px] sm:flex-initial"
              leftIcon={<CalendarIcon className="size-4 opacity-70" />}
            />
          }
        >
          {value === null ? placeholder : rangeLabel(value)}
        </PopoverTrigger>
        <PopoverContent align="center" className="new-ui-theme w-auto p-0">
          <div className="flex flex-col sm:flex-row">
            <div className="flex shrink-0 flex-row gap-1 overflow-x-auto border-b border-border p-2 sm:flex-col sm:border-r sm:border-b-0">
              {onClear ? (
                <Button
                  size="sm"
                  shape="square"
                  variant={value === null ? 'default' : 'ghost'}
                  className="justify-start whitespace-nowrap"
                  onClick={() => {
                    setDraft(undefined)
                    onClear()
                    setOpen(false)
                  }}
                >
                  {placeholder}
                </Button>
              ) : null}
              {presets.map((preset) => (
                <Button
                  // Keyed by label, not by `days`: a caller offering both a
                  // forward and a backward preset of the same length would
                  // otherwise give two children the same key.
                  key={preset.label}
                  size="sm"
                  shape="square"
                  variant={activePreset === preset ? 'default' : 'ghost'}
                  className="justify-start whitespace-nowrap"
                  onClick={() => {
                    applyPreset(preset)
                  }}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
            <div>
              <Calendar
                mode="range"
                selected={draft}
                onSelect={(next: DayPickerRange | undefined, clicked: Date) => {
                  // See `draftAfterClick`: a click on a finished range starts
                  // a new one rather than editing the old one's end.
                  const step = draftAfterClick(draft, next, clicked)
                  setDraft(step.draft)
                  if (step.apply && step.draft?.from && step.draft.to) {
                    onChange({
                      from: startOfDay(step.draft.from),
                      to: startOfDay(step.draft.to),
                    })
                    setOpen(false)
                  }
                }}
                numberOfMonths={isMobile ? 1 : 2}
                defaultMonth={value?.from}
                disabled={{ after: limit }}
                showOutsideDays
              />
              <div className="flex items-center justify-between gap-3 border-t border-border p-3">
                <span className="text-xs text-muted-foreground">
                  {draft?.from
                    ? rangeLabel({
                        from: draft.from,
                        to: draft.to ?? draft.from,
                      })
                    : 'Tap a start day, then an end day'}
                </span>
                <Button
                  size="sm"
                  variant="primary"
                  disabled={!draft?.from}
                  onClick={() => {
                    if (!draft?.from) return
                    onChange({
                      from: startOfDay(draft.from),
                      to: startOfDay(draft.to ?? draft.from),
                    })
                    setOpen(false)
                  }}
                >
                  Apply
                </Button>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
      <Button
        variant="outline"
        shape="square"
        size="icon-lg"
        className="size-11 sm:size-10"
        aria-label={`Next ${String(lengthDays)} days`}
        disabled={atLimit}
        onClick={() => {
          scrub(1)
        }}
      >
        <ChevronRightIcon className="size-4" />
      </Button>
    </div>
  )
}
